<template>
  <div
    v-if="userData"
    class="row"
  >
    <div class="col mr-1">
      <div class="card">
        <a-skeleton
          class="card-header card-header-flex align-items-center p-3"
          :loading="userDataLoading"
          :paragraph="{ rows: 4 }"
          :avatar="{size: 150, shape: 'square'}"
          active
        >
          <ImageView
            v-b-modal.expandAvatar
            :image="userData.image"
            type="full"
            class="img-thumbnail avatarIcon"
            height="150px"
            width="150px"
          />
          <div class="ml-5 flex-grow-1">
            <div class="d-flex justify-content-between">
              <h2 class="mb-4">
                {{ userData.full_name }}
              </h2>
              <div>
                <b-badge
                  :variant="userData.is_active ? 'success' : 'danger'"
                  class="font-size-12 p-1 m-1"
                >
                  {{ userData.is_active ? $t('users.status.active') : $t('users.status.inactive') }}
                </b-badge><br>
                <b-badge
                  v-if="userData.is_blocked"
                  variant="warning"
                  class="font-size-12 p-1 m-1"
                >
                  {{ $t('users.status.blocked') }}
                </b-badge>
              </div>
            </div>
            <ul class="user-data-list">
              <li>
                <a :href="`mailto:${userData.email}`">
                  <a-icon
                    type="mail"
                    class="mr-2"
                  />{{ userData.email ? userData.email : $t('common.not_specified') }}
                </a>
                <a-tag
                  v-if="userData.email"
                  class="ml-2"
                  :color="userData.email_confirmed ? 'green' : 'red'"
                >
                  {{ userData.email_confirmed ? $t('auth.confirmed') : $t('auth.not_confirmed') }}
                </a-tag>
              </li>
              <li>
                <a :href="`tel:${userData.phone}`">
                  <a-icon
                    type="phone"
                    class="mr-2"
                  />{{ userData.phone ? $options.filters.phoneFormatter(userData.phone) : $t('common.not_specified') }}
                </a>
                <a-tag
                  v-if="userData.phone"
                  class="ml-2"
                  :color="userData.phone_confirmed ? 'green' : 'red'"
                >
                  {{ userData.phone_confirmed ? $t('auth.confirmed') : $t('auth.not_confirmed') }}
                </a-tag>
              </li>
              <li>{{ $t('users.date_created') }}: {{ userData.date_created | dateFormatter('YYYY-MM-DD HH:mm') }}</li>
              <li>{{ $t('orders.total_orders') }}: {{ pagination.total }}</li>
            </ul>
          </div>
        </a-skeleton>
        <div class="card-body">
          <b-tabs justified>
            <b-tab
              active
              class="pt-3"
            >
              <template #title>
                <a-icon type="home" /> {{ $t('users.adress_book') }}
              </template>
              <a-list
                item-layout="horizontal"
                :data-source="userData.address_book"
                bordered
              >
                <a-list-item
                  slot="renderItem"
                  slot-scope="item"
                >
                  <a-list-item-meta
                    :description="`${$t('common.added_m')}: ${$options.filters.dateFormatter(item.date_created, 'YYYY-MM-DD HH:mm')}`"
                  >
                    <span slot="title">{{ `${item.zip_code} ${$l(item.region.title)}, ${$l(item.district.title)}, ${item.street}` }}</span>
                  </a-list-item-meta>
                </a-list-item>
              </a-list>
            </b-tab>
            <b-tab class="pt-3">
              <template #title>
                <a-icon type="credit-card" /> {{ $t('users.cards') }}
              </template>
              <a-list
                item-layout="horizontal"
                :data-source="userData.cards"
                bordered
              >
                <a-list-item
                  slot="renderItem"
                  slot-scope="item"
                >
                  <a-list-item-meta
                    :description="`${$t('common.added_f')}: ${$options.filters.dateFormatter(item.date_created, 'YYYY-MM-DD HH:mm')}`"
                  >
                    <div
                      slot="title"
                      class="d-flex"
                    >
                      <span>{{ item.card_pan }}</span>
                      <b-badge
                        v-if="item.is_main"
                        variant="success"
                        class="font-size-12 p-1 ml-auto"
                      >
                        {{ $t('users.card_main') }}
                      </b-badge>
                    </div>
                  </a-list-item-meta>
                </a-list-item>
              </a-list>
            </b-tab>
            <b-tab class="pt-3">
              <template #title>
                <a-icon type="clock-circle" /> {{ $t('users.sessions') }}
              </template>
              <a-skeleton
                :loading="sessionsListLoading"
                :title="false"
                :paragraph="{ rows: 4 }"
                active
              >
                <a-list
                  item-layout="horizontal"
                  :data-source="sessions"
                  bordered
                >
                  <a-list-item
                    slot="renderItem"
                    slot-scope="item"
                  >
                    <a-list-item-meta>
                      <div slot="title">
                        <p class="mb-1">
                          {{ $t('users.application') }}: {{ item.application }}
                        </p>
                        <p class="mb-1">
                          {{ $t('users.ip_address') }}: {{ item.remoteAddress }}
                        </p>
                      </div>
                    </a-list-item-meta>
                    <b-button
                      variant="primary"
                      @click="terminateSessionConfirm(item.accessToken)"
                    >
                      {{ $t('users.terminate_session') }}
                    </b-button>
                  </a-list-item>
                </a-list>
              </a-skeleton>
            </b-tab>
          </b-tabs>
        </div>
      </div>
      <div class="w-100 d-flex justify-content-around">
        <b-button
          :variant="userData.is_blocked ? 'success' : 'warning'"
          @click="showBlockingTogglerConfirm"
        >
          {{ $t(`users.actions.${userData.is_blocked ? 'unblock' : 'block'}`) }}
        </b-button>
        <b-button
          :variant="userData.is_active ? 'danger' : 'success'"
          @click="showActivityTogglerConfirm"
        >
          {{ $t(`users.actions.${userData.is_active ? 'delete' : 'restore'}`) }}
        </b-button>
      </div>
    </div>
    <div class="card col ml-1">
      <div class="card-header">
        <h5 class="mb-0">
          {{ $t('orders.self') }}
        </h5>
      </div>
      <div class="card-body p-0">
        <a-table
          class="product-table"
          :columns="columns"
          :data-source="orders"
          :pagination="pagination"
          :row-key="'id'"
          :scroll="{x: 1280}"
          @change="handleTableChange"
        >
          <div
            slot="filterDropdown"
            slot-scope="{ setSelectedKeys, selectedKeys, confirm, clearFilters, column }"
            class="custom-filter-dropdown"
          >
            <a-input
              v-ant-ref="c => searchInput = c"
              :placeholder="`Search ${column.dataIndex}`"
              :value="selectedKeys[0]"
              style="width: 188px; margin-bottom: 8px; display: block;"
              @change="e => setSelectedKeys(e.target.value ? [e.target.value] : [])"
              @pressEnter="() => handleSearch(selectedKeys, confirm)"
            />
            <a-button
              type="primary"
              size="small"
              style="width: 90px; margin-right: 8px"
              @click="() => handleSearch(selectedKeys, confirm)"
            >
              Search
            </a-button>
            <a-button
              size="small"
              style="width: 90px"
              @click="() => handleReset(clearFilters)"
            >
              Reset
            </a-button>
          </div>
          <a-icon
            slot="filterIcon"
            slot-scope="filtered"
            type="search"
            :style="{ color: filtered ? '#108ee9' : undefined }"
          />
          <template
            slot="id"
            slot-scope="text, record"
          >
            <router-link
              :to="`/admin/seller/orders/${text}`"
              class="btn btn-sm btn-light"
            >
              {{ record.long_id }}
            </router-link>
          </template>
          <template
            slot="date_created"
            slot-scope="text"
          >
            {{ text | dateFormatter('YYYY-MM-DD HH:mm:ss') }}
          </template>
          <template
            slot="company"
            slot-scope="text"
          >
            <router-link
              :to="'/admin/seller/stores/' + text.hash_id"
              class="mr-2"
            >
              <ShrinkStringDisplay :string="$l(text.title)" />
            </router-link>
          </template>
          <template
            slot="amount"
            slot-scope="text"
          >
            <span>{{ text | currency }}</span><small>UZS</small>
          </template>
          <template
            slot="items"
            slot-scope="text"
          >
            <ShrinkStringDisplay
              v-for="(item, index) in text"
              :key="index"
              :string="$l(item)"
            />
          </template>
          <template
            slot="tracking_number"
            slot-scope="text"
          >
            {{ text }}
          </template>
          <template
            slot="status"
            slot-scope="text"
          >
            <OrderStatusTag
              :status="text.code"
              :text="$l(text.title)"
            />
          </template>
        </a-table>
      </div>
    </div>
    <b-modal
      id="expandAvatar"
      :hide-footer="true"
    >
      <div class="d-flex justify-content-center">
        <ImageView
          :image="userData.image"
          type="full"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import userBuyersService from '@/services/api/admin/user-buyers-service'
import ImageView from '@/components/common/ImageView'
import OrderStatusTag from '@/components/order/OrderStatusTag'
import ShrinkStringDisplay from '@/components/common/ShrinkStringDisplay'

export default {
  name: 'Users',
  components: {
    ImageView,
    OrderStatusTag,
    ShrinkStringDisplay,
  },
  data () {
    return {
      userId: this.$route.params.id,
      userData: null,
      isAvatarExpanded: false,
      orders: [],
      sessions: [],
      sessionsListLoading: false,
      userDataLoading: false,
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
      sorting: { col: 'date_created', order: 'desc' },
      columns: [
        {
          title: 'ID',
          dataIndex: 'id',
          width: 80,
          scopedSlots: { customRender: 'id' },
        },
        {
          title: this.$t('common.date'),
          dataIndex: 'date_created',
          sorter: true,
          defaultSortOrder: 'descend',
          scopedSlots: { customRender: 'date_created' },
        },
        {
          title: this.$t('seller.self'),
          dataIndex: 'company',
          scopedSlots: { customRender: 'company' },
        },
        {
          title: this.$t('orders.amount'),
          dataIndex: 'amount',
          scopedSlots: { customRender: 'amount' },
        },
        {
          title: this.$t('product.self_multiple'),
          dataIndex: 'items',
          scopedSlots: { customRender: 'items' },
        },
        {
          title: this.$t('orders.tracking_number'),
          dataIndex: 'tracking_number',
          scopedSlots: { customRender: 'tracking_number' },
        },
        {
          title: this.$t('topBar.status'),
          dataIndex: 'status',
          scopedSlots: { customRender: 'status' },
        },
      ],
    }
  },

  mounted () {
    this.fetchUser()
    this.fetchUserSessions()
    this.queryOrders()
  },

  methods: {
    fetchUser () {
      this.userDataLoading = true
      userBuyersService.getUser(this.userId)
        .then((response) => {
          if (response && response.data && response.data.content) {
            this.userData = response.data.content
          }
          this.userDataLoading = false
        })
    },

    fetchUserSessions () {
      this.sessionsListLoading = true
      userBuyersService.getUserSessions(this.userId)
        .then(response => {
          if (response && response.data && response.data.content) {
            this.sessions = response.data.content
          }
          this.sessionsListLoading = false
        })
    },

    queryOrders () {
      userBuyersService.getUserOrders(this.userId, this.pagination, this.sorting, this.filters)
        .then((response) => {
          if (response && response.data && response.data.content) {
            this.orders = response.data.content
            this.pagination.total = response.data.pagination.total
          }
        })
    },
    handleTableChange(pagination, filter, sorter) {
      if (sorter.columnKey && sorter.order)
        this.sorting = {
          col: sorter.columnKey,
          order: sorter.order.replace('end', ''), // ascend -> asc | descend -> desc
        }

      Object.keys(filter).forEach(key => {
        if (filter[key] && filter[key].length === 1)
          this.filters[key] = filter[key][0]
        else
          this.filters[key] = null
      })

      this.pagination.current = pagination.current

      this.queryOrders()
    },

    terminateSessionConfirm(token) {
      this.$confirm({
        title: `${this.$t('users.terminate_session_confirm')}`,
        okText: this.$t('common.confirm'),
        cancelText: this.$t('common.cancel'),
        onOk: () => this.terminateSession(token),
      })
    },

    terminateSession(token) {
      this.sessionsListLoading = true
      userBuyersService.terminateUserSession(this.userId, token)
        .then(() => {
          this.fetchUserSessions()
        })
    },

    showBlockingTogglerConfirm() {
      this.$confirm({
        title: `${this.$t('common.are_you_sure')}`,
        okText: this.$t('common.confirm'),
        cancelText: this.$t('common.cancel'),
        onOk: this.toggleBlock,
      })
    },

    toggleBlock() {
      this.userDataLoading = true
      userBuyersService.toggleBlocking(this.userId)
        .then(() => {
          this.fetchUser()
        })
    },

    showActivityTogglerConfirm() {
      this.$confirm({
        title: `${this.$t('common.are_you_sure')}`,
        okText: this.$t('common.confirm'),
        cancelText: this.$t('common.cancel'),
        onOk: this.toggleActivity,
      })
    },

    toggleActivity() {
      this.userDataLoading = true
      userBuyersService.toggleActivity(this.userId)
        .then(() => {
          this.fetchUser()
        })
    },
  },
}
</script>

<style scoped>
  .custom-filter-dropdown {
    padding: 8px;
    border-radius: 4px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .highlight {
    background-color: rgb(255, 192, 105);
    padding: 0px;
  }

  .avatarIcon {
    cursor: zoom-in;
  }

  .user-data-list {
    padding-left: 0;
    list-style: none;
  }

  .user-data-list li {
    margin-bottom: .4em;
  }
</style>
